/**
 * Playwright UAT harness seeded MSAL id_token helpers.
 *
 * Seeded-token fallback is allowed ONLY on localhost dev origins when the
 * Playwright init script has set the harness marker. Production hosts must
 * never accept window.__cordexaUatSeededIdToken or sessionStorage bypass paths.
 */

const SKEW_MS = 300_000;

const UAT_LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

declare global {
  interface Window {
    /** Set by Playwright UAT init script before MSAL initialize may clear sessionStorage. */
    __cordexaUatSeededIdToken?: string;
    /** True only when scripts/uat/msal_playwright_cache init script ran. */
    __cordexaUatHarnessActive?: boolean;
  }
}

function decodeJwtExpMs(jwt: string): number {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return (payload?.exp ?? 0) * 1000;
  } catch {
    return 0;
  }
}

function tokenStillValid(jwt: string): boolean {
  if (jwt.split(".").length !== 3) return false;
  const expMs = decodeJwtExpMs(jwt);
  return Boolean(expMs && Date.now() < expMs - SKEW_MS);
}

/** True when seeded-token auth fallback may be considered (localhost SPA dev only). */
export function isUatSeededAuthAllowed(): boolean {
  if (typeof window === "undefined") return false;
  return UAT_LOCAL_HOSTS.has(window.location.hostname.toLowerCase());
}

function harnessActive(): boolean {
  return window.__cordexaUatHarnessActive === true;
}

/**
 * Returns a non-expired id_token from the Playwright UAT harness, if allowed.
 */
export function readSeededMsalIdToken(): string | null {
  if (!isUatSeededAuthAllowed() || !harnessActive()) {
    return null;
  }

  const fromWindow = window.__cordexaUatSeededIdToken;
  if (typeof fromWindow === "string" && tokenStillValid(fromWindow)) {
    return fromWindow;
  }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith("msal.") || !key.toLowerCase().includes("idtoken")) {
        continue;
      }
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const entity = JSON.parse(raw) as { secret?: string };
      const secret = entity?.secret;
      if (typeof secret !== "string" || !tokenStillValid(secret)) {
        continue;
      }
      return secret;
    }
  } catch {
    // ignore parse / access errors
  }
  return null;
}

export function hasSeededMsalSession(): boolean {
  return readSeededMsalIdToken() !== null;
}
